import { useCallback, useState } from 'react'
import { api } from '../api'
import { Icon } from './Icon'
import { TotpQr } from './TotpQr'
import { downloadRecoveryCodes } from '../recovery-codes'

/**
 * The door somebody meets when their workspace requires a second step and
 * they have none.
 *
 * The decision behind this screen was what happens to people already in, and
 * the answer is that enrolment is the gate rather than rejection: nobody is
 * ever told "no" about their own documentation. They are signed in — the
 * session is real, which is what makes enrolling possible at all — and the
 * server refuses everything under `/api/` until an authenticator is
 * confirmed. So this is what the shell shows instead of the product.
 *
 * It says WHOSE decision it was, by name. A person meeting an unexpected wall
 * reads it as a fault unless something tells them otherwise, and "your
 * workspace asked for this" is the difference between a support ticket and a
 * minute with a phone.
 *
 * The way out is one step and it is on this screen. Signing out is the other
 * door, and it is offered plainly rather than hidden — somebody who cannot
 * enrol right now must not be trapped on a screen with nothing to press.
 */
export function EnrolTwoFactor({
  workspace,
  onEnrolled,
  onSignOut,
}: {
  /** The workspace that requires it, named because the sentence needs it. */
  workspace: string | null
  onEnrolled: () => void
  onSignOut: () => void
}) {
  const [enrolling, setEnrolling] = useState<{ readable: string; uri: string } | null>(null)
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const begin = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      const started = await api.startTwoFactor()
      setEnrolling({ readable: started.readable, uri: started.uri })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }, [])

  const finish = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      const { recoveryCodes } = await api.confirmTwoFactor(code.trim())
      /*
       * The recovery codes are shown BEFORE the shell opens, and they are
       * shown once. Dropping somebody straight into the product would spend
       * the only moment these exist — the settings screen can never show them
       * again, because they are hashed the instant they are made.
       */
      setCodes(recoveryCodes)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }, [code])

  if (codes) {
    return (
      <div className="gate" data-enrol-two-factor>
        <div className="gate__identity">
          <span className="gate__mark">
            <Icon name="logo" size={26} />
          </span>
          <h1>Staleless</h1>
        </div>
        <div className="gate__card">
          <h2>Two-step sign-in is on</h2>
          <p className="gate__text">
            Save these recovery codes somewhere that is not this computer. Each one signs you in
            once if your authenticator is lost, and they are shown only now.
          </p>
          <ul className="totp__codes mono" data-recovery-codes>
            {codes.map((one) => (
              <li key={one}>{one}</li>
            ))}
          </ul>
          <div className="settings__row">
            <button
              className="btn btn--primary"
              data-recovery-download
              onClick={() => downloadRecoveryCodes(codes)}
            >
              Download recovery codes
            </button>
            <button className="btn" data-enrol-continue onClick={onEnrolled}>
              I have saved them — continue
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="gate" data-enrol-two-factor>
      <div className="gate__identity">
        <span className="gate__mark">
          <Icon name="logo" size={26} />
        </span>
        <h1>Staleless</h1>
      </div>
      <div className="gate__card">
        <h2>Add a second step</h2>
        <p className="gate__text" data-enrol-why>
          {workspace ?? 'This workspace'} requires two-step sign-in. Nothing is wrong with your
          account and nothing has been lost — set up an authenticator and you are through. Your work
          is exactly where you left it.
        </p>
        {error && (
          <p className="screen__error">
            <Icon name="alert" size={13} /> {error}
          </p>
        )}
        {enrolling ? (
          <>
            <TotpQr uri={enrolling.uri} />
            <p className="gate__text">
              Scan the code with your authenticator. On this device,{' '}
              <a href={enrolling.uri}>open it directly</a>. Or type the key in by hand:
            </p>
            <p className="secret mono" data-totp-secret>
              {enrolling.readable}
            </p>
            <label className="field">
              <span className="label">The six-digit code from your authenticator</span>
              <input
                className="input"
                autoComplete="one-time-code"
                placeholder="Six-digit code"
                value={code}
                disabled={busy}
                data-enrol-code
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
            <button
              className="btn btn--primary"
              disabled={busy}
              data-enrol-confirm
              onClick={() => void finish()}
            >
              {busy ? <Icon name="spinner" size={13} /> : null}
              Turn it on
            </button>
          </>
        ) : (
          <button
            className="btn btn--primary"
            disabled={busy}
            data-enrol-start
            onClick={() => void begin()}
          >
            {busy ? <Icon name="spinner" size={13} /> : null}
            Set up an authenticator
          </button>
        )}
        <button className="btn btn--ghost btn--sm" onClick={onSignOut}>
          Sign out instead
        </button>
      </div>
    </div>
  )
}
